import React, { useRef, useEffect, useCallback, memo } from 'react';
import { Button, Textarea } from '../ui';
import { LoaderCircle, Send, X, Paperclip } from 'lucide-react';
import { cn, debounce } from '../../utils'; 
import { fileToAttachment, validateFile, getAcceptString } from '../../utils/fileUtils';
import { ENABLE_FILE_UPLOAD, CHAT_CONFIG } from '../../utils/constants';
import type { ChatInputProps, FileAttachment as FileAttachmentType } from '../../types/chat';
import './ChatInput.css';

const ChatInputComponent: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSubmit,
  isLoading,
  placeholder = "Введите ваше сообщение...",
  className = "",
  maxLength = 4000,
  attachments = [],
  onAttachmentsChange,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const debouncedResize = debounce((...args: unknown[]) => {
    const textarea = args[0] as HTMLTextAreaElement;
    const maxHeight = 200;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = `${newHeight}px`;
  }, 50);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      const form = e.currentTarget.closest('form');
      form?.requestSubmit();
    }
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    
    if (newValue.length > maxLength) {
      return;
    }
    
    onChange(newValue);
    debouncedResize(e.target);
  }, [onChange, maxLength, debouncedResize]);

  const handleClear = useCallback(() => {
    onChange('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.focus();
    }
  }, [onChange]);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !onAttachmentsChange) return;

    const newAttachments: FileAttachmentType[] = [];
    
    for (const file of Array.from(files)) {
      const validation = validateFile(file);
      if (!validation.valid) {
        alert(validation.error);
        continue;
      }

      try {
        const attachment = await fileToAttachment(file);
        newAttachments.push(attachment);
      } catch (error) {
        console.error('Error processing file:', error);
        alert(`Ошибка при обработке файла ${file.name}: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
      }
    }

    if (newAttachments.length > 0) {
      onAttachmentsChange([...attachments, ...newAttachments]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [attachments, onAttachmentsChange]);

  const handleAttachFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  useEffect(() => {
    if (textareaRef.current && !value) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value]);

  useEffect(() => {
    if (textareaRef.current && !isLoading) {
      textareaRef.current.focus();
    }
  }, [isLoading]);

  const trimmedLength = value.trim().length;
  const hasAttachmentsOnly = ENABLE_FILE_UPLOAD && attachments.length > 0;
  const hasMinText = trimmedLength >= CHAT_CONFIG.MIN_PROMPT_LENGTH;
  const isDisabled = isLoading || (!hasMinText && !hasAttachmentsOnly);
  const isOverLimit = value.length > maxLength * 0.9;
  const showRequiredHint = ENABLE_FILE_UPLOAD && attachments.length > 0 && !value.trim();

  return (
    <form onSubmit={onSubmit} className={cn('chat-input-form', className)}>
      {/* Hidden file input */}
      {ENABLE_FILE_UPLOAD && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept={getAcceptString()}
        />
      )}


      <div className="chat-input-wrapper">
        <div className="chat-input-field">
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={cn(
              'chat-input-textarea',
              isOverLimit && 'chat-input-textarea--warning'
            )}
            disabled={isLoading}
            maxLength={maxLength}
            spellCheck={false}
          />
          
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="chat-input-clear"
              disabled={isLoading}
              aria-label="Clear input"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="chat-input-actions">
          {ENABLE_FILE_UPLOAD && (
            <Button
              type="button"
              onClick={handleAttachFile}
              disabled={isLoading}
              className="chat-input-attach-button"
              aria-label="Attach file"
            >
              <Paperclip size={16} />
            </Button>
          )}
          
          {isOverLimit && !isLoading && (
            <div className="chat-input-counter">
              {value.length}/{maxLength}
            </div>
          )}
          
          <Button 
            type="submit" 
            disabled={isDisabled}
            className={cn(
              'chat-input-button',
              !isDisabled && 'chat-input-button--active'
            )}
            aria-label="Send message"
          >
            {isLoading ? (
              <LoaderCircle size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </Button>
        </div>
      </div>

      {showRequiredHint && (
        <p className="chat-input-helper" role="status">
          Введите текст запроса (минимум {CHAT_CONFIG.MIN_PROMPT_LENGTH} символов) или отправьте только файл.
        </p>
      )}
    </form>
  );
};

export const ChatInput = memo(ChatInputComponent);
